/**
 * The vertical timeline of an enrollment's visits (tracker item 85): item,
 * date, status, technician, price and a link to the job. Shared by the
 * enrollment page and the Bundles cards on the customer and property pages.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../api/types';
import { formatJobDate, formatJobMoney } from '../../utils/jobPresentation';
import { canEditStepPrice, nextVisitText, stepDate, stepStatusLabel, stepStatusTone, stepUnitPrice } from './bundleUi';

const DOT: Record<ReturnType<typeof stepStatusTone>, string> = {
  done: 'bg-green-600 text-white',
  cancelled: 'bg-gray-400 text-white',
  open: 'bg-blue-600 text-white',
  planned: 'bg-white text-gray-600 ring-2 ring-gray-300',
};

interface TimelineProps {
  enrollment: types.BundleEnrollment;
  /** Technician id to display name (email) */
  technicians?: Record<string, string>;
  /** Show the "Next visit in N days" line */
  showNext?: boolean;
  /** Today (YYYY-MM-DD) for the next-visit text */
  today?: string;
  /** Offer "Edit price" on visits whose job can still change */
  onEditPrice?: (step: types.EnrollmentStep) => void;
  /** Link the visit to its job (default true) */
  linkJobs?: boolean;
}

export function EnrollmentTimeline({ enrollment, technicians = {}, showNext = true, today, onEditPrice, linkJobs = true }: TimelineProps) {
  const next = showNext ? nextVisitText(enrollment.nextVisitDate, today) : null;
  return (
    <div>
      {next && <p className="mb-3 text-sm font-medium text-blue-800" data-testid="next-visit">{next}</p>}
      <ol className="space-y-0" aria-label="Visits">
        {enrollment.steps.map((step, index) => {
          const tone = stepStatusTone(step);
          const technician = step.job?.assignedTo ? technicians[step.job.assignedTo] ?? 'Assigned' : step.job ? 'Unassigned' : null;
          const last = index === enrollment.steps.length - 1;
          return (
            <li key={step.step} className="relative flex gap-3 pb-5" data-testid={`timeline-step-${step.step}`}>
              {!last && <span aria-hidden="true" className="absolute left-3.5 top-7 h-full w-px -translate-x-1/2 bg-gray-200" />}
              <span aria-hidden="true" className={`relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${DOT[tone]}`}>{step.step}</span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="font-semibold text-gray-900 break-words">
                    {step.itemName}
                    {Number(step.quantity) !== 1 && <span className="ml-1 font-normal text-gray-600">× {step.quantity}</span>}
                  </p>
                  <p className="text-sm font-medium text-gray-900">{formatJobMoney(step.lineTotal)}</p>
                </div>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                  <span>{formatJobDate(stepDate(step))}</span>
                  <span className={tone === 'done' ? 'text-green-700' : tone === 'cancelled' ? 'text-gray-500 line-through' : ''}>{stepStatusLabel(step)}</span>
                  {technician && <span>{technician}</span>}
                  <span>{formatJobMoney(stepUnitPrice(step))} each</span>
                </p>
                <p className="mt-1 flex flex-wrap items-center gap-x-4 text-sm">
                  {step.job && linkJobs && <Link to={`/jobs/${step.job.id}`} className="font-medium text-blue-700 hover:underline">View job</Link>}
                  {onEditPrice && canEditStepPrice(step) && (
                    <button type="button" className="py-1 font-medium text-blue-700 hover:underline" onClick={() => onEditPrice(step)} aria-label={`Edit price of visit ${step.step}, ${step.itemName}`}>Edit price</button>
                  )}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The progress bar with its `1 of 3 done` caption. */
export function ProgressBar({ progress, label }: { progress: types.EnrollmentProgress; label: string }) {
  const percent = progress.total === 0 ? 0 : Math.round(((progress.completed + progress.cancelled) / progress.total) * 100);
  return (
    <div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.completed + progress.cancelled} aria-label={label}>
        <div className="h-full rounded-full bg-green-600" style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-1 text-sm text-gray-700">{label}</p>
    </div>
  );
}
